import type { Prisma } from '@prisma/client';
import Decimal from 'decimal.js';
import { permissionsGranted, loadAuthContext } from '../auth/auth.middleware.js';
import type { AuthContext } from '../auth/auth.types.js';
import {
  registerImportEntity,
  type ImportColumn,
  type ImportContext,
  type ImportEntity,
  type MissingRef,
  type ParsedRow,
} from '../csv/csv.registry.js';
import { issuesToErrors } from '../parties/parties.import.js';
import { createParty } from '../parties/parties.service.js';
import { partyInputSchema } from '../parties/parties.service.js';
import {
  DOC_PERM,
  approveDocument,
  assertWithinLimit,
  docInclude,
  documentInputSchema,
  isoOf,
  saveDraft,
  type DocKind,
  type DocumentInput,
  type LineInput,
} from './documents.service.js';
import { createReceipt, receiptInputSchema, voidReceipt } from './receipts.service.js';

const isoDate = /^\d{4}-\d{2}-\d{2}$/;

/** Accepts DD/MM/YYYY (as typed in Uganda) or ISO YYYY-MM-DD. */
export function parseDateCell(
  v: string | undefined,
  label: string,
  errors: string[],
  required = true,
): string | null {
  const t = (v ?? '').trim();
  if (!t) {
    if (required) errors.push(`${label}: required`);
    return null;
  }
  let iso: string | null = null;
  const dmy = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(t);
  if (isoDate.test(t)) iso = t;
  else if (dmy) iso = `${dmy[3]}-${dmy[2]!.padStart(2, '0')}-${dmy[1]!.padStart(2, '0')}`;
  if (
    !iso ||
    Number.isNaN(Date.parse(`${iso}T00:00:00Z`)) ||
    new Date(`${iso}T00:00:00Z`).toISOString().slice(0, 10) !== iso
  ) {
    errors.push(`${label}: use DD/MM/YYYY or YYYY-MM-DD`);
    return null;
  }
  return iso;
}

const amountRe = /^\d{1,15}(\.\d{1,4})?$/;
const cleanAmount = (v: string | undefined) => (v ?? '').replace(/,/g, '').trim();

interface DocRowValue {
  kind: DocKind;
  number: string | null;
  clientName: string;
  input: Omit<DocumentInput, 'partyId'>;
  /** SKUs named on lines, kept for reference resolution. */
  skus: { lineIndex: number; sku: string; description: string; price: string }[];
}

const docColumns = (kind: 'quotation' | 'proforma' | 'invoice'): ImportColumn[] => [
  {
    key: 'number',
    label: `${kind === 'invoice' ? 'Invoice' : kind === 'quotation' ? 'Quotation' : 'Proforma'} number`,
    example:
      kind === 'invoice'
        ? 'INV-2026-0001'
        : kind === 'quotation'
          ? 'QUO-2026-0001'
          : 'PRO-2026-0001',
    aliases: [`${kind}_number`, `${kind} number`, 'doc number'],
  },
  {
    key: 'client_name',
    label: 'Client name',
    required: true,
    example: 'Acme Uganda Ltd',
    aliases: ['client', 'customer'],
  },
  {
    key: 'issue_date',
    label: 'Issue date',
    required: true,
    example: '15/03/2026',
    aliases: ['date'],
  },
  ...(kind === 'invoice'
    ? [{ key: 'due_date', label: 'Due date', example: '14/04/2026' } as ImportColumn]
    : [{ key: 'valid_until', label: 'Valid until', example: '14/04/2026' } as ImportColumn]),
  { key: 'currency', label: 'Currency', example: 'UGX' },
  { key: 'exchange_rate', label: 'Exchange rate', example: '' },
  { key: 'reference', label: 'Reference / PO', example: 'PO-4411' },
  { key: 'notes', label: 'Notes', example: '' },
  { key: 'terms', label: 'Terms', example: '' },
  {
    key: 'line_description',
    label: 'Line description',
    required: true,
    example: 'Website build',
    aliases: ['description', 'item'],
  },
  { key: 'item_sku', label: 'Item SKU', example: 'SVC-WEB', aliases: ['sku'] },
  { key: 'quantity', label: 'Quantity', example: '1', aliases: ['qty'] },
  { key: 'unit', label: 'Unit', example: 'each' },
  {
    key: 'unit_price',
    label: 'Unit price',
    required: true,
    example: '2500000',
    aliases: ['price', 'rate'],
  },
  { key: 'discount_percent', label: 'Discount %', example: '0' },
  { key: 'vat_code', label: 'VAT category', example: 'VAT_STD' },
  { key: 'revenue_account', label: 'Revenue account', example: 'REV_SOFTWARE' },
];

const HEADER_FIELDS = [
  'client_name',
  'issue_date',
  'due_date',
  'valid_until',
  'currency',
  'exchange_rate',
  'reference',
] as const;

function parseDocGroup(
  kind: 'quotation' | 'proforma' | 'invoice',
  rows: { rowNumber: number; raw: Record<string, string> }[],
): ParsedRow<DocRowValue> {
  const errors: string[] = [];
  const first = rows[0]!;
  const raw = first.raw;
  const number = raw.number?.trim() || null;

  // Header fields repeat on every row of a document and must agree.
  for (const other of rows.slice(1)) {
    for (const f of HEADER_FIELDS) {
      if (
        (other.raw[f] ?? '').trim().toLowerCase() !== (raw[f] ?? '').trim().toLowerCase() &&
        (other.raw[f] ?? '').trim() !== ''
      ) {
        errors.push(
          `Row ${other.rowNumber}: ${f} differs from row ${first.rowNumber} (all lines of ${number} must match)`,
        );
      }
    }
  }
  const clientName = raw.client_name?.trim() ?? '';
  if (!clientName) errors.push('Client name: required');
  const issueDate = parseDateCell(raw.issue_date, 'Issue date', errors);
  const dueDate = parseDateCell(raw.due_date, 'Due date', errors, false);
  const validUntil = parseDateCell(raw.valid_until, 'Valid until', errors, false);
  const currency = raw.currency?.trim().toUpperCase() || undefined;
  const rate = cleanAmount(raw.exchange_rate);
  if (rate && !/^\d{1,11}(\.\d{1,8})?$/.test(rate)) errors.push('Exchange rate: enter a number');

  const lines: LineInput[] = [];
  const skus: DocRowValue['skus'] = [];
  for (const r of rows) {
    const at = `Row ${r.rowNumber}`;
    const desc = r.raw.line_description?.trim();
    if (!desc) {
      errors.push(`${at}: Line description is required`);
      continue;
    }
    const qty = cleanAmount(r.raw.quantity) || '1';
    const price = cleanAmount(r.raw.unit_price);
    const disc = cleanAmount(r.raw.discount_percent) || '0';
    if (!/^\d{1,11}(\.\d{1,4})?$/.test(qty) || Number(qty) <= 0)
      errors.push(`${at}: Quantity must be a positive number`);
    if (!amountRe.test(price)) errors.push(`${at}: Unit price must be a number`);
    if (!/^\d{1,3}(\.\d{1,4})?$/.test(disc) || Number(disc) > 100)
      errors.push(`${at}: Discount % must be between 0 and 100`);
    const sku = r.raw.item_sku?.trim();
    if (sku) skus.push({ lineIndex: lines.length, sku, description: desc, price });
    lines.push({
      kind: 'item',
      description: desc,
      quantity: qty,
      unit: r.raw.unit?.trim() || null,
      unitPrice: price || '0',
      discountType: Number(disc) > 0 ? 'percent' : 'none',
      discountValue: disc,
      vatCode: r.raw.vat_code?.trim().toUpperCase() || 'VAT_STD',
      isOptional: false,
      isSelected: true,
      whtApplicable: false,
      revenueAccountKey: (r.raw.revenue_account?.trim().toUpperCase() ||
        undefined) as LineInput['revenueAccountKey'],
      saveToCatalogue: false,
    });
  }
  if (errors.length) return { ok: false, errors };

  const parsed = documentInputSchema.omit({ partyId: true }).safeParse({
    issueDate,
    dueDate,
    validUntil,
    currency,
    exchangeRate: rate || undefined,
    reference: raw.reference?.trim() || null,
    notes: raw.notes?.trim() || null,
    terms: raw.terms?.trim() || null,
    lines,
  });
  if (!parsed.success)
    return {
      ok: false,
      errors: issuesToErrors(parsed.error, { lines: 'Lines', issueDate: 'Issue date' }),
    };
  return { ok: true, value: { kind, number, clientName, input: parsed.data, skus } };
}

async function findClient(ctx: ImportContext, name: string) {
  return ctx.tx.party.findFirst({
    where: { companyId: ctx.companyId, name, OR: [{ isClient: true }, { isProspect: true }] },
    select: { id: true, isClient: true },
  });
}

async function authFor(ctx: ImportContext): Promise<AuthContext> {
  const auth = await loadAuthContext(ctx.tx, ctx.userId, ctx.companyId);
  if (!auth) throw new Error('User not found');
  return auth;
}

function docSnapshotInput(
  d: Prisma.SalesDocumentGetPayload<{ include: typeof docInclude }>,
): DocumentInput {
  return {
    partyId: d.partyId,
    contactId: d.contactId,
    currency: d.currency,
    exchangeRate: d.exchangeRate.toString(),
    issueDate: isoOf(d.issueDate)!,
    dueDate: isoOf(d.dueDate),
    validUntil: isoOf(d.validUntil),
    reference: d.reference,
    notes: d.notes,
    terms: d.terms,
    lines: d.lines.map((l) => ({
      kind: l.kind as 'item' | 'section',
      itemId: l.itemId,
      description: l.description,
      quantity: l.quantity.toString(),
      unit: l.unit,
      unitPrice: l.unitPrice.toString(),
      discountType: l.discountType as 'none' | 'percent' | 'amount',
      discountValue: l.discountValue.toString(),
      vatCode: l.vatCode,
      isOptional: l.isOptional,
      isSelected: l.isSelected,
      whtApplicable: l.whtApplicable,
      revenueAccountKey: l.revenueAccountKey as LineInput['revenueAccountKey'],
      saveToCatalogue: false,
    })),
  };
}

function makeDocEntity(kind: 'quotation' | 'proforma' | 'invoice'): ImportEntity<DocRowValue> {
  const columns = docColumns(kind);
  const perm = DOC_PERM[kind];
  return {
    key: `${kind}s`,
    label:
      kind === 'invoice'
        ? 'Invoices'
        : kind === 'quotation'
          ? 'Quotations & Estimates'
          : 'Proforma Invoices',
    importPermission: `${perm}.import`,
    columns,
    groupKey: 'number',
    parseRow: () => ({ ok: false, errors: ['Grouped import expected'] }),
    parseGroup: (rows) => parseDocGroup(kind, rows),
    rowKey: (v) => (v.number ? `${kind}:${v.number.toLowerCase()}` : null),
    async validate(ctx, v) {
      const errors: string[] = [];
      if (ctx.options.asApproved) {
        const auth = await authFor(ctx);
        if (!permissionsGranted(auth.permissions, `${perm}.approve`))
          errors.push(`You do not have permission to import ${kind}s as approved`);
      }
      // VAT categories and revenue accounts must exist.
      const rates = await ctx.tx.taxRate.findMany({
        where: { companyId: ctx.companyId, kind: 'vat', active: true },
        select: { code: true },
      });
      const codes = new Set(rates.map((r) => r.code));
      v.input.lines.forEach((l, i) => {
        if (!codes.has(l.vatCode))
          errors.push(`Line ${i + 1}: VAT category "${l.vatCode}" does not exist`);
        if (
          l.revenueAccountKey &&
          !['REV_SOFTWARE', 'REV_CONSULT', 'REV_HOSTING', 'REV_EQUIP', 'REV_OTHER'].includes(
            l.revenueAccountKey,
          )
        )
          errors.push(`Line ${i + 1}: unknown revenue account "${l.revenueAccountKey}"`);
      });
      if (v.number && ctx.options.duplicate === 'update') {
        const existing = await ctx.tx.salesDocument.findFirst({
          where: { companyId: ctx.companyId, docType: kind, number: v.number },
        });
        if (existing && existing.status !== 'draft')
          errors.push(
            `${v.number} is already issued and cannot be updated. Use skip or void it first.`,
          );
      }
      if (v.input.currency && v.input.currency !== 'UGX' && !v.input.exchangeRate) {
        const latest = await ctx.tx.exchangeRate.findFirst({
          where: { companyId: ctx.companyId, currency: v.input.currency },
        });
        if (!latest)
          errors.push(
            `No exchange rate for ${v.input.currency}: add a column or set one in Settings`,
          );
      }
      return errors;
    },
    async findExisting(ctx, v) {
      if (!v.number) return null;
      const d = await ctx.tx.salesDocument.findFirst({
        where: { companyId: ctx.companyId, docType: kind, number: v.number },
        include: docInclude,
      });
      return d ? { id: d.id, snapshot: docSnapshotInput(d) } : null;
    },
    async missingRefs(ctx, v) {
      const refs: MissingRef[] = [];
      if (!(await findClient(ctx, v.clientName)))
        refs.push({ kind: 'client', value: v.clientName });
      for (const s of v.skus) {
        if (
          !(await ctx.tx.item.findUnique({
            where: { companyId_sku: { companyId: ctx.companyId, sku: s.sku } },
          }))
        )
          refs.push({ kind: 'item', value: s.sku, hint: `${s.description}|${s.price}` });
      }
      return refs;
    },
    async createRef(ctx, ref) {
      if (ref.kind === 'client') {
        const party = await createParty(ctx.tx, {
          companyId: ctx.companyId,
          userId: ctx.userId,
          needsReview: true,
          input: partyInputSchema.parse({ name: ref.value, isClient: true }),
        });
        return party.id;
      }
      const [description = ref.value, price = '0'] = (ref.hint ?? '').split('|');
      const item = await ctx.tx.item.create({
        data: {
          companyId: ctx.companyId,
          name: description.slice(0, 200),
          sku: ref.value,
          type: 'service',
          needsReview: true,
          createdBy: ctx.userId,
          prices: { create: [{ currency: 'UGX', price: amountRe.test(price) ? price : '0' }] },
        },
      });
      return item.id;
    },
    async removeRef(ctx, ref) {
      try {
        if (ref.kind === 'client') await ctx.tx.party.delete({ where: { id: ref.id } });
        else await ctx.tx.item.delete({ where: { id: ref.id } });
        return null;
      } catch {
        return `a ${ref.kind} created by this import is now used elsewhere`;
      }
    },
    async create(ctx, v, refs) {
      const auth = await authFor(ctx);
      const client =
        (await findClient(ctx, v.clientName))?.id ??
        refs.get(`client:${v.clientName}`) ??
        ctx.options.missingRef.map?.[v.clientName];
      if (!client) throw new Error(`Client "${v.clientName}" could not be resolved`);
      const lines = v.input.lines.map((l) => ({ ...l }));
      for (const s of v.skus) {
        const item = await ctx.tx.item.findUnique({
          where: { companyId_sku: { companyId: ctx.companyId, sku: s.sku } },
        });
        const id = item?.id ?? refs.get(`item:${s.sku}`) ?? ctx.options.missingRef.map?.[s.sku];
        if (id) lines[s.lineIndex]!.itemId = id;
      }
      // Party visibility is by role; an import writes on behalf of the signed-in user.
      const created = await saveDraft(ctx.tx, {
        auth,
        kind,
        input: { ...v.input, lines, partyId: client },
      });
      if (v.number)
        await ctx.tx.salesDocument.update({
          where: { id: created.id },
          data: { number: v.number },
        });
      if (ctx.options.asApproved) {
        assertWithinLimit(auth, created.baseTotal);
        await approveDocument(ctx.tx, auth, created.id);
      }
      return { id: created.id };
    },
    async update(ctx, id, v, refs) {
      const auth = await authFor(ctx);
      const client =
        (await findClient(ctx, v.clientName))?.id ?? refs.get(`client:${v.clientName}`);
      if (!client) throw new Error(`Client "${v.clientName}" could not be resolved`);
      await saveDraft(ctx.tx, { auth, kind, id, input: { ...v.input, partyId: client } });
    },
    async restore(ctx, id, before) {
      const auth = await authFor(ctx);
      await saveDraft(ctx.tx, { auth, kind, id, input: before as DocumentInput });
    },
    async remove(ctx, id) {
      const d = await ctx.tx.salesDocument.findFirst({ where: { id, companyId: ctx.companyId } });
      if (!d) return null;
      if (d.status !== 'draft')
        return `${d.number ?? 'a document'} has already been issued or sent; void it instead of undoing the import`;
      await ctx.tx.salesDocumentLine.deleteMany({ where: { documentId: id } });
      await ctx.tx.salesDocument.delete({ where: { id } });
      return null;
    },
  };
}

// ───────────────────────── payment receipts ─────────────────────────

interface ReceiptRowValue {
  receiptNo: string | null;
  clientName: string;
  account: string;
  input: Omit<typeof receiptInputSchema._type, 'partyId' | 'financialAccountId'>;
  invoices: { number: string; amount: string }[];
}

const receiptColumns: ImportColumn[] = [
  {
    key: 'receipt_number',
    label: 'Receipt number',
    example: 'RCT-2026-0001',
    aliases: ['receipt no'],
  },
  {
    key: 'client_name',
    label: 'Client name',
    required: true,
    example: 'Acme Uganda Ltd',
    aliases: ['client'],
  },
  {
    key: 'receipt_date',
    label: 'Receipt date',
    required: true,
    example: '20/03/2026',
    aliases: ['date'],
  },
  {
    key: 'account',
    label: 'Received into (account name)',
    required: true,
    example: 'Stanbic Main',
    aliases: ['bank', 'account name'],
  },
  { key: 'amount', label: 'Amount received', required: true, example: '2950000' },
  { key: 'wht_withheld', label: 'Tax withheld by client', example: '0' },
  { key: 'currency', label: 'Currency', example: 'UGX' },
  { key: 'exchange_rate', label: 'Exchange rate', example: '' },
  { key: 'reference', label: 'Bank / MoMo reference', example: 'STB-88221' },
  { key: 'invoice_number', label: 'Invoice paid', example: 'INV-2026-0001', aliases: ['invoice'] },
  {
    key: 'allocated_amount',
    label: 'Amount allocated to that invoice',
    example: '2950000',
    aliases: ['allocated'],
  },
];

function parseReceiptGroup(
  rows: { rowNumber: number; raw: Record<string, string> }[],
): ParsedRow<ReceiptRowValue> {
  const errors: string[] = [];
  const first = rows[0]!;
  const raw = first.raw;
  for (const other of rows.slice(1)) {
    for (const f of [
      'client_name',
      'receipt_date',
      'account',
      'amount',
      'wht_withheld',
      'currency',
      'reference',
    ]) {
      if (
        (other.raw[f] ?? '').trim() !== '' &&
        (other.raw[f] ?? '').trim().toLowerCase() !== (raw[f] ?? '').trim().toLowerCase()
      )
        errors.push(`Row ${other.rowNumber}: ${f} differs from row ${first.rowNumber}`);
    }
  }
  const clientName = raw.client_name?.trim() ?? '';
  if (!clientName) errors.push('Client name: required');
  const account = raw.account?.trim() ?? '';
  if (!account) errors.push('Received into: required');
  const date = parseDateCell(raw.receipt_date, 'Receipt date', errors);
  const amount = cleanAmount(raw.amount);
  if (!amountRe.test(amount) || Number(amount) <= 0)
    errors.push('Amount received: enter a positive number');
  const wht = cleanAmount(raw.wht_withheld) || '0';
  if (!amountRe.test(wht)) errors.push('Tax withheld: enter a number');
  const rate = cleanAmount(raw.exchange_rate);
  const invoices: ReceiptRowValue['invoices'] = [];
  for (const r of rows) {
    const inv = r.raw.invoice_number?.trim();
    if (!inv) continue;
    const alloc = cleanAmount(r.raw.allocated_amount);
    if (!amountRe.test(alloc) || Number(alloc) <= 0)
      errors.push(`Row ${r.rowNumber}: allocated amount for ${inv} must be a positive number`);
    else invoices.push({ number: inv, amount: alloc });
  }
  if (errors.length) return { ok: false, errors };
  return {
    ok: true,
    value: {
      receiptNo: raw.receipt_number?.trim() || null,
      clientName,
      account,
      invoices,
      input: {
        receiptDate: date!,
        currency: raw.currency?.trim().toUpperCase() || undefined,
        exchangeRate: rate || undefined,
        amount,
        whtWithheld: wht,
        reference: raw.reference?.trim() || null,
        notes: null,
        allocations: [],
        autoAllocate: false,
        proformaId: null,
      },
    },
  };
}

const receiptEntity: ImportEntity<ReceiptRowValue> = {
  key: 'receipts',
  label: 'Payment Receipts',
  importPermission: 'sales.receipt.import',
  columns: receiptColumns,
  groupKey: 'receipt_number',
  parseRow: () => ({ ok: false, errors: ['Grouped import expected'] }),
  parseGroup: parseReceiptGroup,
  rowKey: (v) => (v.receiptNo ? `receipt:${v.receiptNo.toLowerCase()}` : null),
  async validate(ctx, v) {
    const errors: string[] = [];
    const account = await ctx.tx.financialAccount.findFirst({
      where: { companyId: ctx.companyId, name: v.account, status: 'active' },
    });
    if (!account) errors.push(`Received into: no active account named "${v.account}"`);
    const client = await findClient(ctx, v.clientName);
    if (client) {
      for (const a of v.invoices) {
        const inv = await ctx.tx.salesDocument.findFirst({
          where: { companyId: ctx.companyId, docType: 'invoice', number: a.number },
        });
        if (!inv) errors.push(`Invoice ${a.number} does not exist`);
        else if (inv.partyId !== client.id)
          errors.push(`Invoice ${a.number} belongs to a different client`);
        else if (!['approved', 'sent', 'partially_paid'].includes(inv.status))
          errors.push(
            `Invoice ${a.number} is ${inv.status.replace('_', ' ')} and cannot receive payments`,
          );
        else if (
          new Decimal(a.amount).gt(
            new Decimal(inv.total.toString())
              .minus(inv.amountPaid.toString())
              .minus(inv.amountCredited.toString()),
          )
        )
          errors.push(`Amount for ${a.number} is more than what is owed`);
      }
    }
    const total = v.invoices.reduce((s, a) => s.plus(a.amount), new Decimal(0));
    if (total.gt(new Decimal(v.input.amount).plus(v.input.whtWithheld)))
      errors.push('The allocated amounts are more than the amount received');
    if (v.receiptNo && ctx.options.duplicate === 'update') {
      const dup = await ctx.tx.paymentReceipt.findFirst({
        where: { companyId: ctx.companyId, receiptNo: v.receiptNo },
      });
      if (dup)
        errors.push(
          `${v.receiptNo} already exists and posted receipts cannot be updated. Use skip or fail.`,
        );
    }
    return errors;
  },
  async findExisting(ctx, v) {
    if (!v.receiptNo) return null;
    const r = await ctx.tx.paymentReceipt.findFirst({
      where: { companyId: ctx.companyId, receiptNo: v.receiptNo },
    });
    return r ? { id: r.id, snapshot: null } : null;
  },
  async missingRefs(ctx, v) {
    return (await findClient(ctx, v.clientName)) ? [] : [{ kind: 'client', value: v.clientName }];
  },
  async createRef(ctx, ref) {
    const party = await createParty(ctx.tx, {
      companyId: ctx.companyId,
      userId: ctx.userId,
      needsReview: true,
      input: partyInputSchema.parse({ name: ref.value, isClient: true }),
    });
    return party.id;
  },
  async removeRef(ctx, ref) {
    try {
      await ctx.tx.party.delete({ where: { id: ref.id } });
      return null;
    } catch {
      return 'a client created by this import is now used elsewhere';
    }
  },
  async create(ctx, v, refs) {
    const auth = await authFor(ctx);
    const partyId =
      (await findClient(ctx, v.clientName))?.id ??
      refs.get(`client:${v.clientName}`) ??
      ctx.options.missingRef.map?.[v.clientName];
    if (!partyId) throw new Error(`Client "${v.clientName}" could not be resolved`);
    const account = await ctx.tx.financialAccount.findFirstOrThrow({
      where: { companyId: ctx.companyId, name: v.account },
    });
    const allocations: { invoiceId: number; amount: string }[] = [];
    for (const a of v.invoices) {
      const inv = await ctx.tx.salesDocument.findFirstOrThrow({
        where: { companyId: ctx.companyId, docType: 'invoice', number: a.number },
      });
      allocations.push({ invoiceId: inv.id, amount: a.amount });
    }
    // Posts the ledger entry immediately (PLAN.md section 9: receipt import posts after the dry-run is approved).
    const receipt = await createReceipt(
      ctx.tx,
      auth,
      { ...v.input, partyId, financialAccountId: account.id, allocations },
      { receiptNo: v.receiptNo ?? undefined },
    );
    return { id: receipt.id };
  },
  async update() {
    throw new Error('Posted receipts cannot be updated by import');
  },
  async restore() {
    /* never updated */
  },
  /** Rolling back a posted receipt voids it (a reversal), it is never deleted. */
  async remove(ctx, id) {
    const auth = await authFor(ctx);
    try {
      await voidReceipt(ctx.tx, auth, id, 'Import rolled back');
      return null;
    } catch (e) {
      return e instanceof Error ? e.message : 'could not void the receipt';
    }
  },
};

registerImportEntity(makeDocEntity('quotation'));
registerImportEntity(makeDocEntity('proforma'));
registerImportEntity(makeDocEntity('invoice'));
registerImportEntity(receiptEntity);
